import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc, 
  deleteDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL,
  getStorage 
} from 'firebase/storage';
import { auth, db } from '../firebase';
import { Bien } from '../types';
import { dossiersService } from './dossiersService';

// Sous-dossiers Property Assist à créer automatiquement
const PROPERTY_ASSIST_SOUS_DOSSIERS = [
  { id: 'propertyassistsousdossier1', nom: "ASSURANCES" },
  { id: 'propertyassistsousdossier2', nom: "CRÉDITS" },
  { id: 'propertyassistsousdossier3', nom: "ÉNERGIES" },
  { id: 'propertyassistsousdossier4', nom: "TÉLÉCOMS" },
];

class BiensService {
  private collectionName = 'biens';

  // Vérifier si l'utilisateur est authentifié
  isUserAuthenticated(): boolean {
    return !!auth.currentUser;
  }

  // Upload d'image vers Firebase Storage
  async uploadImage(file: File, onProgress?: (progress: number) => void): Promise<string> {
    try {
      const storage = getStorage();
      const fileName = `${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `habitations/${fileName}`);
      
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            if (onProgress) {
              onProgress(progress);
            }
          },
          (error) => {
            console.error('❌ Erreur upload:', error);
            reject(error);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log('✅ Image uploadée:', downloadURL);
            resolve(downloadURL);
          }
        );
      });
    } catch (error) {
      console.error('❌ Erreur lors de l\'upload de l\'image:', error);
      throw error;
    }
  }

  // Récupérer un bien par son ID
  async getBienById(bienId: string): Promise<Bien | null> {
    try {
      const bienRef = doc(db, this.collectionName, bienId);
      const snap = await getDoc(bienRef);
      if (!snap.exists()) return null;
      const data: any = snap.data();
      return {
        id: snap.id,
        firebaseId: snap.id,
        nom: data.nom,
        description: data.description,
        type: data.type,
        image: data.image,
        userId: data.userId,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date()
      };
    } catch (error) {
      console.error('❌ Erreur lors de la récupération du bien par ID:', error);
      throw error;
    }
  }

  // Obtenir l'ID de l'utilisateur actuel
  getCurrentUserId(): string | null {
    return auth.currentUser?.uid || null;
  }

  // Ajouter un nouveau bien
  async ajouterBien(bien: Omit<Bien, 'id' | 'firebaseId' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<string> {
    if (!this.isUserAuthenticated()) {
      throw new Error('Utilisateur non authentifié');
    }

    const userId = this.getCurrentUserId();
    if (!userId) {
      throw new Error('Impossible de récupérer l\'ID utilisateur');
    }

    try {
      const bienData = {
        ...bien,
        userId,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, this.collectionName), bienData);
      console.log('✅ Bien ajouté avec succès, ID:', docRef.id);
      
      // Créer automatiquement le dossier Property Assist avec ses sous-dossiers
      try {
        // Créer les sous-dossiers
        for (const sousDossier of PROPERTY_ASSIST_SOUS_DOSSIERS) {
          await dossiersService.creerDossier({
            nom: sousDossier.nom,
            description: '',
            bienId: docRef.id,
            parentId: null,
            categorie: 'gestioncourante'
          });
        }
        
        console.log('✅ Sous-dossiers Property Assist créés');
      } catch (error) {
        console.error('❌ Erreur lors de la création des dossiers Property Assist:', error);
        // Ne pas bloquer la création du bien si la création des dossiers échoue
      }
      
      return docRef.id;
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout du bien:', error);
      throw error;
    }
  }

  // Récupérer tous les biens de l'utilisateur
  async getBiensUtilisateur(): Promise<Bien[]> {
    if (!this.isUserAuthenticated()) {
      throw new Error('Utilisateur non authentifié');
    }

    const userId = this.getCurrentUserId();
    if (!userId) {
      throw new Error('Impossible de récupérer l\'ID utilisateur');
    }

    try {
      // Requête simple sans orderBy pour éviter le besoin d'index composite
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      const biens: Bien[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        biens.push({
          id: doc.id,
          firebaseId: doc.id,
          nom: data.nom,
          description: data.description,
          type: data.type,
          image: data.image,
          userId: data.userId,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date()
        });
      });

      // Trier côté client par date de création (plus récent en premier)
      biens.sort((a, b) => {
        const dateA = a.createdAt || new Date(0);
        const dateB = b.createdAt || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      console.log('✅ Biens récupérés:', biens.length);
      return biens;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des biens:', error);
      throw error;
    }
  }

  // Supprimer un bien
  async supprimerBien(bienId: string): Promise<void> {
    if (!this.isUserAuthenticated()) {
      throw new Error('Utilisateur non authentifié');
    }

    try {
      const bienRef = doc(db, this.collectionName, bienId);
      await deleteDoc(bienRef);
      console.log('✅ Bien supprimé avec succès');
    } catch (error) {
      console.error('❌ Erreur lors de la suppression du bien:', error);
      throw error;
    }
  }

  // Mettre à jour un bien
  async mettreAJourBien(bienId: string, updates: Partial<Bien>): Promise<void> {
    if (!this.isUserAuthenticated()) {
      throw new Error('Utilisateur non authentifié');
    }

    try {
      const bienRef = doc(db, this.collectionName, bienId);
      const updateData = {
        ...updates,
        updatedAt: new Date()
      };
      
      await updateDoc(bienRef, updateData);
      console.log('✅ Bien mis à jour avec succès');
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour du bien:', error);
      throw error;
    }
  }

  // Alias pour compatibilité
  async modifierBien(bienId: string, updates: Partial<Bien>): Promise<void> {
    return this.mettreAJourBien(bienId, updates);
  }
}

const biensService = new BiensService();
export default biensService;